import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import ExitApplicationLink from './ExitApplicationLink';

const STATUS = {
  met: {
    label: 'Yes',
    status: 'success',
    statusText: 'Requirements met',
  },
  notMet: {
    label: 'No',
    status: 'error',
    statusText: 'Requirements not met',
  },
};

const ELIGIBILITY_CHECKS = [
  {
    key: 'view:sponsorIsDisabled',
    requirementMet: value => value === true,
    metText:
      'the Veteran is permanently and totally disabled from a service-connected disability, or the Veteran died from a service-connected disability, or the Veteran, at the time of their death, was rated permanently and totally disabled from a service-connected disability.',
    notMetText:
      'the Veteran is not or was not permanently and totally disabled from a service-connected disability. Or they did not die from a service-connected disability.',
  },
  {
    key: 'view:tricareEnrollment',
    requirementMet: value => value !== true,
    metText:
      'the Veteran or applicants aren’t eligible for or enrolled in TRICARE.',
    notMetText:
      'the Veteran or applicants are eligible for or enrolled in TRICARE. This means they can’t get CHAMPVA benefits.',
    showWhenNotMet: true,
  },
];

const buildEligibilityItems = formData =>
  ELIGIBILITY_CHECKS.map(check => {
    const requirementMet = check.requirementMet(formData?.[check.key]);
    const status = requirementMet ? STATUS.met : STATUS.notMet;

    return {
      key: check.key,
      requirementMet,
      showWhenNotMet: check.showWhenNotMet,
      content: requirementMet ? check.metText : check.notMetText,
      ...status,
    };
  });

const renderEligibilityAlert = (status, headline, body) => (
  <va-alert status={status}>
    <h2 slot="headline">{headline}</h2>
    <p className="vads-u-margin-y--0">{body}</p>
  </va-alert>
);

const EligibilitySummary = ({ formData = {} }) => {
  const items = buildEligibilityItems(formData);
  const allRequirementsMet = items.every(item => item.requirementMet);

  const eligibilityListItems = useMemo(
    () =>
      items.map(
        (
          {
            key,
            status,
            statusText,
            content,
            label,
            requirementMet,
            showWhenNotMet,
          },
          index,
        ) => (
          <li
            key={key}
            className={classNames({
              'vads-u-margin-top--2': items.length > 1 && index > 0,
            })}
          >
            <va-card className="vads-u-display--flex vads-u-margin-bottom--2">
              <va-tag-status status={status} text={statusText} />
              <p>
                <strong>{label}</strong>, {content}
              </p>
              {!requirementMet && showWhenNotMet && index === 1 && (
                <div
                  className="vads-u-margin-top--2"
                  data-testid="1010d-tricare-link"
                >
                  <p>You can apply for healthcare through TRICARE instead.</p>
                  <va-link
                    href="https://www.tricare.mil/Plans/Eligibility"
                    text="Learn more about TRICARE eligibility on the TRICARE website"
                    external
                  />
                </div>
              )}
            </va-card>
          </li>
        ),
      ),
    [items],
  );

  return (
    <>
      {allRequirementsMet
        ? renderEligibilityAlert(
            'success',
            'You may be eligible for this benefit',
            <>
              Based on your answers, you may be eligible. Select{' '}
              <strong>Continue</strong> to complete your application.
            </>,
          )
        : renderEligibilityAlert(
            'warning',
            'You may not meet all eligibility requirements',
            'Based on your answers, you may not meet all the requirements for this benefit. You can exit this application or continue to complete it.',
          )}
      <h3 className="vads-u-font-size--h3">
        Based on the Veteran’s information, the applicant(s) may{' '}
        {allRequirementsMet ? '' : 'not '}
        be eligible
      </h3>
      <p>Here’s how we checked eligibility:</p>
      <ul className="no-bullets vads-u-margin-bottom--2">
        {eligibilityListItems}
      </ul>

      <va-details label="What to do if the applicants don’t meet the eligibility requirements">
        <p>
          The applicants must meet all the requirements so far to be eligible
          for CHAMPVA benefits.
        </p>
        <p>
          If you think the eligibility summary is wrong, select{' '}
          <strong>Back</strong> to check your answers.
        </p>
        <p>
          If you think they’re still eligible, you can continue with this
          application.
        </p>
      </va-details>

      <p>
        <strong>If they haven’t met the requirements so far</strong>, you can
        exit this application now.
      </p>
      <p>
        <ExitApplicationLink location="eligibility-summary" />
      </p>
      <p>
        <strong>If they’ve met the requirements so far</strong>, you can
        continue with this application.
      </p>
    </>
  );
};

EligibilitySummary.propTypes = {
  formData: PropTypes.shape({
    'view:tricareEnrollment': PropTypes.bool,
    'view:sponsorIsDisabled': PropTypes.bool,
  }),
};

export default EligibilitySummary;
